import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { X } from 'lucide-react';

const ConfirmModal = ({
  isOpen,
  onConfirm = () => {},
  onCancel = () => {},
  headerText = 'Confirm',
  bodyText = '',
  bodySubText = '',
  btnConfirmText = 'Confirm',
  btnCancelText = 'Cancel',
}) => {
  const modalRef = useRef(null);
  const previouslyFocused = useRef(null);
  const prevBodyOverflow = useRef(null);

  useEffect(() => {
    if (!isOpen) return;

    // save currently focused element
    previouslyFocused.current = document.activeElement;
    // lock background scroll
    prevBodyOverflow.current = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // focus first focusable in modal (and fall back to modal for screen readers)
    const focusable = modalRef.current?.querySelectorAll(
      'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
    );
    focusable && focusable[0]?.focus();
    // ensure the dialog itself can receive focus for SR / keyboard (helps mobile a11y)
    modalRef.current?.setAttribute('tabindex', '-1');
    modalRef.current?.focus();

    const handleKey = (e) => {
      if (e.key === 'Escape') {
        onCancel();
      } else if (e.key === 'Tab') {
        // basic focus trap
        if (!modalRef.current) return;
        const nodes = modalRef.current.querySelectorAll(
          'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
        );
        if (!nodes.length) return;
        const first = nodes[0];
        const last = nodes[nodes.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    window.addEventListener('keydown', handleKey);
    return () => {
      window.removeEventListener('keydown', handleKey);
      // restore scroll and focus
      document.body.style.overflow = prevBodyOverflow.current || '';
      previouslyFocused.current?.focus?.();
      // cleanup tabindex we added
      modalRef.current?.removeAttribute('tabindex');
    };
  }, [isOpen, onCancel]);

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-modal-title"
      aria-describedby="confirm-modal-desc"
    >
      {/* backdrop */}
      <div
        className="absolute inset-0 bg-dark/80 backdrop-blur-sm"
        onClick={onCancel}
        aria-hidden="true"
      />

      {/* dialog: bottom-sheet on mobile, centered on sm+ */}
      <div
        ref={modalRef}
        className="relative z-10 w-full max-w-full sm:max-w-[570px] bg-white rounded-t-lg sm:rounded-[20px] shadow-lg overflow-hidden max-h-[90vh] flex flex-col"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        role="document"
        aria-describedby="confirm-modal-desc"
      >
        <div className="bg-primary text-white px-4 py-3 sm:py-4 flex justify-between items-center">
          <h3 id="confirm-modal-title" className="text-lg font-semibold">
            {headerText}
          </h3>
          <button
            onClick={onCancel}
            aria-label="Close"
            className="p-2 rounded-md text-white hover:text-red-300 focus:outline-none focus:ring-2 focus:ring-white"
          >
            <X size={20} />
          </button>
        </div>

        <div id="confirm-modal-desc" className="px-4 py-4 text-center overflow-auto">
          {bodyText && <p className="mb-2 text-lg leading-normal text-body-color">{bodyText}</p>}
          {bodySubText && <p className="text-sm font-medium leading-5 text-[#404145]">{bodySubText}</p>}
        </div>

        <div className="px-4 py-4 border-t">
          {/* Buttons: stacked on mobile (primary at bottom), side-by-side on sm+ */}
          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-center">
            <div className="w-full sm:w-1/2 px-3">
              <button
                onClick={onCancel}
                className="w-full rounded-md border border-stroke px-4 py-3 text-sm font-medium text-dark bg-white hover:border-red-600 hover:bg-red-600 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary"
              >
                {btnCancelText}
              </button>
            </div>

            <div className="w-full sm:w-1/2 px-3">
              <button
                onClick={onConfirm}
                className="w-full rounded-md px-4 py-3 text-sm font-medium text-white bg-primary hover:bg-blue-dark transition-colors focus:outline-none focus:ring-2 focus:ring-primary"
              >
                {btnConfirmText}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ConfirmModal;
